import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { drawnScale } from "../lib/drawn-scale";
import { longestFit } from "../lib/longest-fit";

const ELLIPSIS = "…";

/**
 * How far past an edge, in drawn pixels, text has to reach to count as
 * overflowing it. Browsers lay text out in 1/64px or 1/60px units, so this
 * only absorbs the rounding a transform adds to the rects.
 */
const OVERFLOW_TOLERANCE = 0.001;

/**
 * Index just past the last character whose right edge fits within `limit`
 * pixels of `left`. Characters are measured in the rendered text node, so
 * kerning, letter spacing and the CJK fallback face are already applied.
 */
function fittingLength(node: Text, left: number, limit: number) {
  const range = document.createRange();
  let low = 0;
  let high = node.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    range.setStart(node, middle - 1);
    range.setEnd(node, middle);
    const right = range.getBoundingClientRect().right - left;
    if (right <= limit) low = middle;
    else high = middle - 1;
  }
  return low;
}

/** The first `length` characters of `text`, ending in an ellipsis. */
function clampedText(text: string, length: number) {
  return text.slice(0, length).trimEnd() + ELLIPSIS;
}

/** Right edge of the whole text node, as drawn. */
function textRight(node: Text) {
  const range = document.createRange();
  range.selectNodeContents(node);
  return range.getBoundingClientRect().right;
}

/**
 * Single-line text that ends in an ellipsis when it does not fit, like
 * `text-overflow: ellipsis`, but whose box ends on the ellipsis. The CSS clip
 * keeps the full-width box and leaves up to a glyph of blank space inside it,
 * which added to the gap before the icon that follows a truncated label.
 *
 * The element keeps its own `overflow: hidden; text-overflow: ellipsis`, which
 * still applies before the first measurement. The full text stays in `title`
 * and, for assistive technology, in a visually hidden copy.
 */
export function ClampedText({
  text,
  as: Tag = "span"
}: {
  text: string;
  as?: "span" | "strong";
}) {
  const ref = useRef<HTMLElement>(null);
  const [measureKey, setMeasureKey] = useState(0);
  // A result belongs to the text and width it was measured at; any change
  // renders the full text again and measures that.
  const key = `${measureKey}\u0000${text}`;
  const [result, setResult] = useState<{ key: string; length: number } | null>(null);
  const length = result?.key === key ? result.length : null;
  const clamped = length === null ? null : clampedText(text, length);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || length !== null) return;
    const node = element.firstChild;
    if (!(node instanceof Text)) return;

    // The ranges, the probe and the bounding rect are measured as drawn; the
    // border and padding are layout sizes, so those are brought to the drawn
    // size. The edges are read from the rect rather than from `clientWidth`,
    // which rounds to a whole pixel: rounded up, it let a character that
    // overflowed by a fraction of a pixel through, and the CSS ellipsis took
    // over again.
    const style = getComputedStyle(element);
    const edges = () => {
      const box = element.getBoundingClientRect();
      const scale = drawnScale(box, style);
      return {
        left:
          box.left +
          (Number.parseFloat(style.borderLeftWidth) + Number.parseFloat(style.paddingLeft)) *
            scale,
        right:
          box.right -
          (Number.parseFloat(style.borderRightWidth) + Number.parseFloat(style.paddingRight)) *
            scale
      };
    };
    const overflows = () => textRight(node) > edges().right + OVERFLOW_TOLERANCE;
    if (!overflows()) return;

    const probe = document.createElement("span");
    probe.textContent = ELLIPSIS;
    probe.style.cssText = "position:absolute;visibility:hidden;padding:0;margin:0";
    element.appendChild(probe);
    const ellipsisWidth = probe.getBoundingClientRect().width;
    probe.remove();

    // That estimate sets the last character's edge in the full text against
    // an ellipsis measured on its own. Set next to each other, the two can land
    // a fraction of a pixel either way, which was enough to keep a character
    // the box did not hold or to drop one it did. So the label is tried in
    // place as it will render and keeps as many characters as fit.
    const { left, right } = edges();
    const estimate = fittingLength(node, left, right - left - ellipsisWidth);
    let fit: number;
    try {
      fit = longestFit(estimate, text.length, (count) => {
        node.data = clampedText(text, count);
        return !overflows();
      });
    } finally {
      node.data = text;
    }
    setResult({ key, length: fit });
  }, [key, length, text]);

  useLayoutEffect(() => {
    let disposed = false;
    const remeasure = () => setMeasureKey((key) => key + 1);
    // Under reduced motion a property change runs as a 0.01ms transition that
    // still reads its old value until a frame has run, so after a resize or a
    // language switch that started any, the label is fitted again once they
    // finish.
    const remeasureAfterTransitions = () => {
      remeasure();
      const transitions = document
        .getAnimations()
        .filter((animation) => animation instanceof CSSTransition)
        .map((animation) => animation.finished);
      if (transitions.length === 0) return;
      void Promise.allSettled(transitions).then(() => {
        if (!disposed) remeasure();
      });
    };
    // Below its 1120px cap the overlay's width follows the window, so a resize
    // starts one of those transitions on it.
    window.addEventListener("resize", remeasureAfterTransitions);
    void document.fonts.ready.then(() => {
      if (!disposed) remeasure();
    });
    // A language switch restyles the overlay through the root's
    // `data-language`, which the provider writes only after this component has
    // measured the new copy, so the room a label was fitted to changes with no
    // resize and no change to the label itself.
    const language = new MutationObserver(remeasureAfterTransitions);
    language.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-language"]
    });
    return () => {
      disposed = true;
      window.removeEventListener("resize", remeasureAfterTransitions);
      language.disconnect();
    };
  }, []);

  if (clamped === null) {
    return <Tag ref={ref as never}>{text}</Tag>;
  }
  return (
    <Fragment>
      <Tag ref={ref as never} title={text} aria-hidden="true">
        {clamped}
      </Tag>
      <em className="sr-only">{text}</em>
    </Fragment>
  );
}
